import React, { useState } from 'react';
import { Plane, Globe, Info, Shield, MapPin, Radar, Satellite, Clock } from 'lucide-react';
import { MetaTags } from '../../components/seo/MetaTags';
import { StructuredData } from '../../components/seo/StructuredData';

export function TrackingPage() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showGlobalMap, setShowGlobalMap] = useState(false);

  const fetchWithRetry = async (url: string, retries = 3): Promise<Response> => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
          },
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Aircraft not found with this tail number.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        if (!text || text.trim().length === 0) {
          throw new Error("No data received for this tail number.");
        }
        
        return response;
      } catch (err) {
        console.warn(`Attempt ${i + 1} failed:`, err);
        
        if (i === retries - 1) {
          if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
            throw new Error("Unable to connect to tracking service. Please check your internet connection and try again.");
          }
          throw err;
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
    throw new Error("Failed to fetch aircraft data after multiple attempts.");
  };

  const handleSearch = async () => {
    const tailNumber = (document.getElementById('tailNumber') as HTMLInputElement)?.value?.trim();
    const errorElement = document.getElementById('error');
    const embedIframe = document.getElementById('embed') as HTMLIFrameElement;
    const placeholder = document.getElementById('placeholder');

    // Reset previous state
    setError(null);
    if (errorElement) errorElement.style.display = "none";
    if (embedIframe) embedIframe.style.display = "none";
    if (placeholder) placeholder.style.display = "none";

    if (!tailNumber) {
      const message = "Please enter a valid tail number.";
      setError(message);
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = "block";
      }
      return;
    }

    setIsLoading(true);
    if (placeholder) placeholder.style.display = "block";

    try {
      // Validate tail number format
      if (!/^[A-Za-z0-9-]+$/.test(tailNumber)) {
        throw new Error("Invalid tail number format. Please use only letters, numbers, and hyphens.");
      }

      // Fetch ICAO hex code with retry mechanism
      const response = await fetchWithRetry(
        `https://hexdb.io/reg-hex?reg=${encodeURIComponent(tailNumber.toLowerCase())}`
      );

      // Get the hex code and convert to lowercase for consistency
      const text = await response.text();
      const hex = text.trim().toLowerCase(); // Extracts the hex code from the response body

      if (!hex) {
        throw new Error("No ICAO hex code found for this tail number.");
      }

      // Update iframe with the hex code
      if (embedIframe) {
        embedIframe.src = `https://adsb.lol/?icao=${hex}`;
        embedIframe.style.display = "block";
        setShowGlobalMap(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch aircraft data. Please try again.";
      setError(message);
      console.warn('Aircraft tracking error:', { error: err, tailNumber });
      
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = "block";
      }

      // Open the global map even if there's an error
      if (embedIframe) {
        embedIframe.src = 'https://adsb.lol';
        embedIframe.style.display = "block";
        setShowGlobalMap(true);
      }
    } finally {
      setIsLoading(false);
      if (placeholder) placeholder.style.display = "none";
    }
  };

  const handleShowGlobalMap = () => {
    const embedIframe = document.getElementById('embed') as HTMLIFrameElement;
    if (embedIframe) {
      embedIframe.src = 'https://adsb.lol';
      embedIframe.style.display = "block";
      setShowGlobalMap(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      <MetaTags
        title="Track Blocked Aircraft & Live Flight Tracking | AirPortal"
        description="Track any aircraft in real-time, including blocked planes and private jets. Advanced tracking system for monitoring aircraft registration, position, altitude, speed, and route data. Free global coverage."
        keywords={[
          'aircraft tracking',
          'blocked aircraft tracking',
          'track blocked planes',
          'private jet tracking',
          'flight tracker',
          'live flight tracking',
          'real-time aircraft position',
          'aviation tracking',
          'tail number lookup',
          'global air traffic',
          'flight monitoring',
          'blocked tail numbers'
        ]}
      />

      <StructuredData 
        type="WebApplication" 
        data={{
          name: "AirPortal Aircraft Tracking",
          applicationCategory: "Transportation/Aviation",
          operatingSystem: "Any",
          offers: {
            price: "0",
            priceCurrency: "USD"
          }
        }} 
      />

      <main className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-10"></div>
          
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 sm:w-80 sm:h-80 bg-primary-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 sm:w-80 sm:h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-white/80 text-sm font-medium mb-8">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                Real-time Global Coverage
              </div>
              
              <div className="inline-flex p-4 bg-gradient-to-r from-primary-500/20 to-blue-500/20 backdrop-blur-sm rounded-3xl mb-8">
                <Radar className="h-12 w-12 sm:h-16 sm:w-16 text-white animate-pulse" />
              </div>
              
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                <span className="block">Advanced Aircraft</span>
                <span className="block bg-gradient-to-r from-primary-300 via-blue-300 to-primary-300 bg-clip-text text-transparent">
                  Tracking System
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto mb-12 leading-relaxed">
                Track any aircraft in real-time, including blocked planes and private jets. 
                Professional-grade tracking with global coverage and advanced analytics.
              </p>

              {/* Search Box */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-2xl blur-xl group-hover:from-primary-500/30 group-hover:to-blue-500/30 transition-all duration-300"></div>
                  <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
                    <div className="flex">
                      <input
                        type="text"
                        id="tailNumber"
                        placeholder="Enter Tail Number (e.g., N154EA)"
                        onKeyPress={handleKeyPress}
                        className="flex-1 px-6 py-4 sm:py-5 text-white placeholder-white/60 bg-transparent focus:outline-none text-lg"
                        disabled={isLoading}
                      />
                      <button
                        onClick={handleSearch}
                        disabled={isLoading}
                        className="px-8 sm:px-10 bg-gradient-to-r from-primary-500 to-blue-600 hover:from-primary-400 hover:to-blue-500 text-white font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="hidden sm:inline">Tracking...</span>
                          </div>
                        ) : (
                          <span>Track</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Show Global Map Button */}
                <button
                  onClick={handleShowGlobalMap}
                  disabled={isLoading}
                  className="group inline-flex items-center gap-3 px-6 py-3 mt-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl border border-white/20 text-white transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Globe className="h-5 w-5" />
                  <span>Show Global Map</span>
                </button>

                <p id="error" className="mt-4 text-red-300 text-sm hidden bg-red-500/20 backdrop-blur-sm rounded-lg p-3 border border-red-500/30"></p>
                <p id="placeholder" className="mt-4 text-primary-200 text-sm hidden flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-300 border-t-transparent rounded-full animate-spin"></div>
                  Loading tracking data...
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* Map Container */}
          <div className="relative mb-16">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-3xl blur-xl"></div>
            <iframe
              id="embed"
              title="Aircraft Tracking Map"
              className="relative w-full h-[500px] sm:h-[600px] rounded-2xl shadow-2xl border border-white/20 hidden bg-white/5 backdrop-blur-sm"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals"
              allow="geolocation; fullscreen; clipboard-write; web-share"
              loading="lazy"
            />
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Shield,
                title: 'Track Blocked Aircraft',
                description: 'Access comprehensive tracking data for blocked and private aircraft while respecting privacy protocols.',
                color: 'from-green-400 to-emerald-600'
              },
              {
                icon: Satellite,
                title: 'Global Coverage',
                description: 'Track aircraft worldwide with our extensive network of receivers and data sources.',
                color: 'from-blue-400 to-cyan-600'
              },
              {
                icon: Clock,
                title: 'Real-Time Updates',
                description: 'Get live position updates, altitude data, and route information for any aircraft.',
                color: 'from-purple-400 to-indigo-600'
              }
            ].map((feature, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl blur-xl group-hover:from-white/10 group-hover:to-white/20 transition-all duration-300"></div>
                <div className="relative bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/20 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <div className={`inline-flex p-4 bg-gradient-to-r ${feature.color} rounded-xl mb-6 shadow-lg`}>
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Tips Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-3xl blur-xl"></div>
            <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/20">
              <h3 className="text-2xl sm:text-3xl font-semibold text-white mb-8 flex items-center gap-4">
                <div className="p-3 bg-gradient-to-r from-primary-500 to-blue-600 rounded-xl">
                  <Info className="h-6 w-6 text-white" />
                </div>
                Professional Tracking Tips
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  'Enter the complete tail number (e.g., N154EA) for most accurate results',
                  'For blocked aircraft, you may see limited or delayed information',
                  'Use the global view to see all active flights in your area of interest',
                  'Some aircraft may temporarily disappear from tracking due to coverage areas',
                  'Military and government aircraft may have restricted tracking data',
                  'Private jets often use blocking services to limit public visibility'
                ].map((tip, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-2 bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-lg mt-1">
                      <div className="w-2 h-2 bg-gradient-to-r from-primary-400 to-blue-500 rounded-full"></div>
                    </div>
                    <p className="text-white/80 leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}